import { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Tag } from "../../studio/ui";
import { FiX, FiGithub, FiExternalLink } from "react-icons/fi";

/* ============================================================
   PROJECT DETAILS — Divine Futurism stained-glass reliquary.
   Frosted violet panel, gold halo, full scripture of the work.
============================================================ */

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(6, 6, 11, 0.8);
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.28s ease;

  @media ${({ theme }) => theme.bp.sm} {
    padding: 14px;
  }
`;

const Panel = styled.div`
  position: relative;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  overflow-y: auto;
  background: linear-gradient(
    180deg,
    rgba(20, 16, 38, 0.92),
    rgba(11, 10, 24, 0.94)
  );
  border: 1px solid ${({ theme }) => theme.borderHi};
  border-radius: 22px;
  backdrop-filter: blur(18px);
  box-shadow: 0 0 0 1px ${({ theme }) => theme.border},
    0 30px 90px -24px ${({ theme }) => theme.violetGlow},
    0 0 70px -16px ${({ theme }) => theme.goldGlow};
  animation: ${rise} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

const Close = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.borderHi};
  background: rgba(11, 10, 24, 0.7);
  color: ${({ theme }) => theme.goldSoft};
  font-size: 20px;
  display: grid;
  place-items: center;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: all 0.25s ease;
  box-shadow: 0 0 20px -6px ${({ theme }) => theme.goldGlow};

  &:hover {
    color: ${({ theme }) => theme.gold};
    border-color: ${({ theme }) => theme.gold};
    box-shadow: 0 0 28px -4px ${({ theme }) => theme.goldGlow};
    transform: rotate(90deg);
  }
`;

const Media = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.veil};
  border-radius: 22px 22px 0 0;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  overflow: hidden;

  iframe,
  img {
    width: 100%;
    height: 100%;
    border: 0;
    object-fit: cover;
    display: block;
  }
`;

const Body = styled.div`
  padding: 30px 32px 34px;

  @media ${({ theme }) => theme.bp.sm} {
    padding: 24px 20px 28px;
  }

  .cat {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 11.5px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: ${({ theme }) => theme.gold};
  }
  h2 {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-size: clamp(30px, 5vw, 42px);
    font-weight: 600;
    line-height: 1.08;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.white};
    margin: 8px 0 6px;
    text-shadow: 0 0 30px ${({ theme }) => theme.goldGlow};
  }
  .tagline {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 13px;
    letter-spacing: 0.03em;
    color: ${({ theme }) => theme.textMuted};
  }
  p.desc {
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 15.5px;
    line-height: 1.75;
    color: ${({ theme }) => theme.textMuted};
    margin-top: 20px;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0;
`;

const Members = styled.div`
  margin-bottom: 24px;

  .h {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 11.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.goldSoft};
    margin-bottom: 10px;
  }
  .names {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .m {
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 13px;
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.glassHi};
    border: 1px solid ${({ theme }) => theme.border};
    padding: 6px 13px;
    border-radius: 999px;
    backdrop-filter: blur(6px);
  }
`;

const Links = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  border-top: 1px solid ${({ theme }) => theme.border};
  padding-top: 24px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 14px;
    font-weight: 600;
    padding: 12px 22px;
    border-radius: 10px;
    transition: all 0.28s ease;
  }
  .primary {
    color: #0b0a14;
    background: linear-gradient(
      100deg,
      ${({ theme }) => theme.goldSoft},
      ${({ theme }) => theme.gold}
    );
    box-shadow: 0 0 26px -6px ${({ theme }) => theme.goldGlow};
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 0 38px -2px ${({ theme }) => theme.goldGlow};
    }
  }
  .ghost {
    color: ${({ theme }) => theme.white};
    border: 1px solid ${({ theme }) => theme.borderHi};
    background: ${({ theme }) => theme.glass};
    backdrop-filter: blur(8px);
    &:hover {
      color: ${({ theme }) => theme.gold};
      border-color: ${({ theme }) => theme.gold};
      box-shadow: 0 0 24px -8px ${({ theme }) => theme.goldGlow};
      transform: translateY(-2px);
    }
  }
`;

export default function ProjectDetails({ openModal, setOpenModal }) {
  const project = openModal?.project;
  const close = () => setOpenModal({ state: false, project: null });

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!project) return null;

  return (
    <Overlay onClick={close}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <Close onClick={close} aria-label="Close">
          <FiX />
        </Close>

        {(project.video || project.image) && (
          <Media>
            {project.video ? (
              <iframe
                src={project.video}
                title={project.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <img src={project.image} alt={project.title} />
            )}
          </Media>
        )}

        <Body>
          <div className="cat">{project.category}</div>
          <h2>{project.title}</h2>
          {project.tagline && <div className="tagline">{project.tagline}</div>}
          <p className="desc">{project.description}</p>

          <Tags>
            {project.tags.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </Tags>

          {project.member && (
            <Members>
              <div className="h">Team</div>
              <div className="names">
                {project.member.map((m) => (
                  <span className="m" key={m.name}>
                    {m.name}
                  </span>
                ))}
              </div>
            </Members>
          )}

          {(project.github || project.webapp) && (
            <Links>
              {project.github && (
                <a
                  className="ghost"
                  href={project.github}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiGithub /> View Code
                </a>
              )}
              {project.webapp && (
                <a
                  className="primary"
                  href={project.webapp}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiExternalLink /> Live Demo
                </a>
              )}
            </Links>
          )}
        </Body>
      </Panel>
    </Overlay>
  );
}
